import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  AutoInviteRule,
  AutoInviteScope,
  DEFAULT_MATCHING_CONDITIONS,
} from './autoInviteTypes'
import { getAutoInviteRule, saveAutoInviteRule } from './autoInviteActions'
import { RuleField, RuleOperator, ConditionJoin } from 'features/RuleBuilder'

export interface AutoInviteState {
  rule: AutoInviteRule | null
  ruleLoaded: boolean
  saveLoading: boolean
}

export const initialState: AutoInviteState = {
  rule: null,
  ruleLoaded: false,
  saveLoading: false,
}

const normalizeRule = (raw: AutoInviteRule): AutoInviteRule => ({
  ...raw,
  scope: raw.scope ?? 'ALL_NEW',
  field: (raw.field as RuleField | null) ?? null,
  operator: (raw.operator as RuleOperator | null) ?? null,
  value: raw.value ?? '',
  secondaryField: (raw.secondaryField as RuleField | null) ?? null,
  secondaryOperator: (raw.secondaryOperator as RuleOperator | null) ?? null,
  secondaryValue: raw.secondaryValue ?? null,
  conditionJoin: (raw.conditionJoin as ConditionJoin | null) ?? null,
  tertiaryField: (raw.tertiaryField as RuleField | null) ?? null,
  tertiaryOperator: (raw.tertiaryOperator as RuleOperator | null) ?? null,
  tertiaryValue: raw.tertiaryValue ?? null,
  tertiaryConditionJoin:
    (raw.tertiaryConditionJoin as ConditionJoin | null) ?? null,
})

const autoInviteSlice = createSlice({
  name: 'autoInvite',
  initialState,
  reducers: {
    getAutoInviteRuleSuccess(state, action: PayloadAction<AutoInviteRule[]>) {
      const first = action.payload?.[0]
      state.rule = first ? normalizeRule(first) : null
      state.ruleLoaded = true
    },
    getAutoInviteRuleFailed(state) {
      state.ruleLoaded = true
    },
    updateLocalRule(state, action: PayloadAction<Partial<AutoInviteRule>>) {
      if (!state.rule) return
      state.rule = { ...state.rule, ...action.payload }
    },
    setScope(state, action: PayloadAction<AutoInviteScope>) {
      if (!state.rule) return
      const scope = action.payload
      if (scope === 'ALL_NEW') {
        state.rule = {
          ...state.rule,
          scope,
          field: null,
          operator: null,
          value: null,
          secondaryField: null,
          secondaryOperator: null,
          secondaryValue: null,
          conditionJoin: null,
          tertiaryField: null,
          tertiaryOperator: null,
          tertiaryValue: null,
          tertiaryConditionJoin: null,
        }
        return
      }
      state.rule = {
        ...state.rule,
        scope,
        ...DEFAULT_MATCHING_CONDITIONS,
        field: state.rule.field ?? DEFAULT_MATCHING_CONDITIONS.field,
        operator: state.rule.operator ?? DEFAULT_MATCHING_CONDITIONS.operator,
        value: state.rule.value ?? DEFAULT_MATCHING_CONDITIONS.value,
      }
    },
    setSaveLoading(state, action: PayloadAction<boolean>) {
      state.saveLoading = action.payload
    },
    saveAutoInviteRuleSuccess(state, action: PayloadAction<AutoInviteRule>) {
      state.rule = normalizeRule(action.payload)
      state.saveLoading = false
    },
    saveAutoInviteRuleFailed(state) {
      state.saveLoading = false
    },
  },
})

export const autoInviteReducer = autoInviteSlice.reducer
export const autoInviteActions = {
  ...autoInviteSlice.actions,
  getAutoInviteRule,
  saveAutoInviteRule,
}
